import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router";
import {
  DEFAULT_DESCRIPTION,
  SITE_NAME,
  SITE_ORIGIN,
} from "../config/site";

function fullPageTitle(title) {
  const t = (title || SITE_NAME).trim();
  if (t.includes(SITE_NAME)) return t;
  return `${t} — ${SITE_NAME}`;
}

/**
 * Управление title, description, canonical, Open Graph / Twitter.
 * @param {string} title — заголовок страницы (без суффикса сайта)
 * @param {string} [description]
 * @param {string} [canonicalUrl] — полный URL; иначе берётся текущий маршрут
 * @param {boolean} [noindex]
 * @param {string} [ogImage] — абсолютный URL картинки
 * @param {string} [ogType] — website | article | product
 * @param {object|object[]} [jsonLd] — объект(ы) для script type="application/ld+json"
 */
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalUrl,
  noindex = false,
  keywords,
  ogImage,
  ogType = "website",
  jsonLd,
}) {
  const location = useLocation();
  const canonical =
    canonicalUrl ||
    `${SITE_ORIGIN}${location.pathname}`;
  const pageTitle = fullPageTitle(title);
  const desc = (description || DEFAULT_DESCRIPTION).slice(0, 320);
  const metaKeywords = Array.isArray(keywords)
    ? keywords.filter(Boolean).join(", ")
    : (keywords || "").trim();

  const ldJson =
    jsonLd == null
      ? null
      : JSON.stringify(Array.isArray(jsonLd) ? jsonLd : [jsonLd]);

  return (
    <Helmet prioritizeSeoTags htmlAttributes={{ lang: "ru" }}>
      <title>{pageTitle}</title>
      <meta name="description" content={desc} />
      {metaKeywords ? <meta name="keywords" content={metaKeywords} /> : null}
      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large" />
      )}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content="ru_RU" />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}

      <meta name="twitter:card" content={ogImage ? "summary_large_image" : "summary"} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {ldJson ? (
        <script type="application/ld+json">{ldJson}</script>
      ) : null}
    </Helmet>
  );
}
